import { LinkOutlined, PrinterOutlined } from '@ant-design/icons';
import { Card, Divider, List, Rate, Skeleton, Space } from 'antd';
import * as React from 'react';
import { memo, useCallback, useContext, useState } from 'react';
import { NO_DATA } from '../../knowledge-base-common/knowledge-base-common.constants';
import { round } from '../../knowledge-base-common/knowledge-base-common.utils';
import { getPrintForm, updateRating } from '../actions';
import { FullArticle, PrinFormDTO } from '../knowledge-base.type';
import { getArticleLink } from '../knowledge-base.utils';
import ArticleStatistics from './article-statistics';
import { localize } from '/react4xp/mixins/helpers';
import Button from '/react4xp/mixins/ui/button/button';
import { ComponentContext } from '/react4xp/parts/knowledge-base/knowledge-base';

interface ArticleViewProps {
    article: FullArticle | null;
    section: string;
    category: string;
    tag: string;
    isAuthorized: boolean;
    lang: string;
}

const ArticleView = memo(
    ({ article, section, category, tag, isAuthorized, lang }: ArticleViewProps) => {
        const [myVote, setMyVote] = useState<{
            rating: number;
            scores: number;
        }>(null);
        const [isVoteLoading, setIsVoteLoading] = useState(false);

        const [isDownloading, setIsDownloading] = useState(false);
        const { clientUrl, serviceUrl, translations, commonTranslations } =
            useContext(ComponentContext);

        // useEffect(() => {
        //     setIsLoading(true);
        //     getById<FullArticle>(serviceUrl, article.id)
        //         .then(setFullArticle)
        //         .finally(() => setIsLoading(false));
        // }, [article]);

        const handleVote = useCallback(async (value: number) => {
            if (!article) return;
            if (value === 0 || myVote !== null) return;
            setIsVoteLoading(true);
            try {
                const { rating, scores } = await updateRating(
                    '/_/service/isnareactcms/kb-article-rating',
                    article.id,
                    value
                );
                setMyVote({ rating, scores });
            } finally {
                setIsVoteLoading(false);
            }
        }, []);

        if (!article) return <Skeleton />;

        const scoreDescription = `${localize('rating', translations)}: ${
            round(myVote?.rating || article?.rating || 0) ?? NO_DATA
        }/5 (${myVote?.scores || article?.scores || localize('no-scores', translations)})`;

        const getPrintFormPdf = async () => {
            setIsDownloading(true);

            const pdfDto: PrinFormDTO = {
                title: article?.title,
                content: article?.content,
                updatedAt: article?.updatedAt,
                section,
                category,
                tag,
                url: getArticleLink(article?.id, clientUrl),
            };

            // const isVerified = await executeRecaptcha('knowledge_base_print_pdf');
            // if (!isVerified) {
            //     return;
            // }

            const pdf = (await getPrintForm(pdfDto, { lang: lang.toUpperCase() })).data;

            const filename = `${article?.title}.pdf`;
            const url = window.URL.createObjectURL(new Blob([pdf]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            setIsDownloading(false);
        };

        return (
            <Card style={{ position: 'relative' }}>
                <ArticleStatistics
                    published={article?.updatedAt}
                    views={article?.views}
                />
                <p
                    className="title"
                    style={{ margin: '1rem 0' }}
                >
                    {article?.title}
                </p>
                <div
                    className="article-view-content"
                    dangerouslySetInnerHTML={{ __html: article?.content }}
                    style={{ wordBreak: 'break-word', wordWrap: 'break-word' }}
                />
                {article?.linked.length > 0 && (
                    <>
                        <Divider />
                        <List
                            size="small"
                            header={<div>{localize('linked-articles', translations)}</div>}
                            bordered
                            dataSource={article?.linked}
                            renderItem={(item) => (
                                <List.Item>
                                    <a href={getArticleLink(item.value, clientUrl)}>
                                        <Space>
                                            <LinkOutlined />
                                            {item.label}
                                        </Space>
                                    </a>
                                </List.Item>
                            )}
                        />
                    </>
                )}
                <Divider />
                <Space direction="vertical">
                    {isAuthorized ? (
                        myVote === null && !article?.myVote ? (
                            <p className="muted">{localize('cta.rate', translations)}</p>
                        ) : null
                    ) : (
                        <p className="muted">
                            {localize('knowledge-base.authorize-to-rate', translations)}
                        </p>
                    )}
                    <Space
                        align="end"
                        size={12}
                    >
                        {isAuthorized && (
                            <Rate
                                allowHalf
                                defaultValue={+(article?.myVote?.rating || '0')}
                                onChange={handleVote}
                                disabled={
                                    isVoteLoading ||
                                    !isAuthorized ||
                                    !!article.myVote ||
                                    myVote !== null
                                }
                            />
                        )}
                    </Space>
                    <p className="muted">{scoreDescription}</p>
                </Space>

                <Button
                    icon={<PrinterOutlined />}
                    style={{ position: 'absolute', top: 16, right: 16 }}
                    onClick={getPrintFormPdf}
                    loading={isDownloading}
                >
                    {localize('button.print', commonTranslations)}
                </Button>
            </Card>
        );
    }
);

export default ArticleView;
